import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { RouteComponentProps, Link } from "react-router-dom";
import { isSupported } from "twilio-video";
import {
  Box,
  Button,
  Grid,
  Container,
  HStack,
  VStack,
  Spacer
} from "@chakra-ui/react";

import { BasicLayout } from "layouts";
import { useToast } from "hooks";
import { useChatContext, useVideoContext } from "hooks/session";
import { joinSession } from "api/webservice/sessions";
import VideoPreview from "./VideoPreview";
import AudioPreview from "./AudioPreview";

interface Props {
  sessionId: string;
}

const SessionCallPreview: React.FC<RouteComponentProps<Props>> = ({
  match
}: RouteComponentProps<Props>) => {
  const { sessionId } = match.params;
  const [loadingJoinSession, setLoadingJoinSession] = useState(false);
  const toast = useToast();
  const { connect: chatConnect } = useChatContext();
  const { connect: videoConnect, getAudioAndVideoTracks } = useVideoContext();

  const history = useHistory();

  useEffect(() => {
    getAudioAndVideoTracks();
  }, []);

  const connectToSession = (): void => {
    setLoadingJoinSession(true);
    joinSession(sessionId)
      .then(async token => {
        await videoConnect(token);
        await chatConnect(token);
        history.push("join");
      })
      .catch(error => {
        setLoadingJoinSession(false);
        toast({
          title: "Error joining session",
          description: "Please refresh the page and try again.",
          status: "error"
        });
        console.log(error.response);
      });
  };

  return (
    <BasicLayout>
      {isSupported ? (
        <Container
          maxWidth="1080px"
          justifyContent="center"
          centerContent
          minH="100vh"
          px={[6, 0]}
          py={[12, 0]}
        >
          <VStack spacing={10} width="85%">
            <Box>Choose your video and audio options</Box>
            <Grid width="100%" templateColumns="repeat(2, 1fr)" gap={4}>
              <VideoPreview />
              <Grid templateRows="repeat(3, fit-content(100%))" gap={3}>
                <AudioPreview />
                <Spacer />
                <HStack justifySelf="flex-end" alignSelf="flex-end" spacing={2}>
                  <Link to="/sessions">
                    <Button as="a" variant="outline" size="sm">
                      Close
                    </Button>
                  </Link>
                  <Button
                    variant="blue"
                    isLoading={loadingJoinSession}
                    onClick={connectToSession}
                    size="sm"
                  >
                    Join Session
                  </Button>
                </HStack>
              </Grid>
            </Grid>

            <Grid>
              <Box fontSize="large">Everything Look Good?</Box>
              <Box>
                Ensure that your camera and microphone are working as expected.
                You should be able to see yourself in the video feed and the
                audio feed should react to you speaking aloud.
                <br />
                <br />
                If you are having issues, you may need to grant your browser
                permission to access your camera and microphone. Alternatively,
                another application or tab may be reserving your devices. You
                can try closing all other applications and / or restarting your
                browser.
              </Box>
            </Grid>
          </VStack>
        </Container>
      ) : (
        <h1>This browser is not supported.</h1>
      )}
    </BasicLayout>
  );
};

export default SessionCallPreview;
